<template>
  <section class="legal-section">
    <div class="legal-container">
      <p class="legal-meta">
        Last updated: {{ updated }}
        <span v-if="jurisdiction"> &middot; {{ jurisdiction }}</span>
      </p>

      <div class="legal-body">
        <slot />
      </div>

      <div class="legal-contact">
        <h2>Questions about this policy</h2>
        <p>
          Write to us at
          <a :href="`mailto:${site.email}`">{{ site.email }}</a>, call
          <a :href="`tel:${site.phone}`">{{ site.phoneDisplay }}</a>, or visit
          {{ site.address.street }}, {{ site.address.locality }},
          {{ site.address.region }} {{ site.address.postalCode }}.
        </p>
      </div>
    </div>
  </section>
</template>

<script setup>
import { defineProps } from 'vue';
import { site } from '@/data/site';

defineProps({
  updated: {
    type: String,
    required: true,
  },
  jurisdiction: {
    type: String,
    default: '',
  },
});
</script>

<style scoped>
.legal-section {
  background: #fff;
  padding: 80px 0 100px;
  color: #1a1a1a;
}

.legal-container {
  max-width: 820px;
  margin: 0 auto;
  padding: 0 24px;
}

.legal-meta {
  font-size: 0.8rem;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  font-weight: 700;
  color: #b91c1c;
  margin: 0 0 36px;
}

.legal-body :deep(h2) {
  font-size: 1.45rem;
  font-weight: 800;
  color: #111;
  margin: 44px 0 14px;
  line-height: 1.3;
}

.legal-body :deep(h2:first-child) {
  margin-top: 0;
}

.legal-body :deep(h3) {
  font-size: 1.08rem;
  font-weight: 700;
  color: #222;
  margin: 26px 0 10px;
}

.legal-body :deep(p),
.legal-body :deep(li) {
  font-size: 1rem;
  line-height: 1.75;
  color: #444;
}

.legal-body :deep(p) {
  margin: 0 0 16px;
}

.legal-body :deep(ul) {
  margin: 0 0 18px;
  padding-left: 22px;
}

.legal-body :deep(li) {
  margin-bottom: 8px;
}

.legal-body :deep(a) {
  color: #b91c1c;
  font-weight: 600;
  text-decoration: underline;
}

.legal-body :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 0 0 24px;
  font-size: 0.92rem;
}

.legal-body :deep(th),
.legal-body :deep(td) {
  border: 1px solid #e5e7eb;
  padding: 12px 14px;
  text-align: left;
  vertical-align: top;
  color: #444;
}

.legal-body :deep(th) {
  background: #f8fafc;
  font-weight: 700;
  color: #111;
}

.legal-body :deep(.callout) {
  background: #fdf5f5;
  border-left: 4px solid #b91c1c;
  padding: 18px 22px;
  border-radius: 4px;
  margin: 0 0 24px;
}

.legal-body :deep(.callout p:last-child) {
  margin-bottom: 0;
}

.legal-contact {
  margin-top: 55px;
  padding-top: 30px;
  border-top: 1px solid #e5e7eb;
}

.legal-contact h2 {
  font-size: 1.2rem;
  font-weight: 800;
  margin: 0 0 10px;
  color: #111;
}

.legal-contact p {
  font-size: 1rem;
  line-height: 1.75;
  color: #444;
  margin: 0;
}

.legal-contact a {
  color: #b91c1c;
  font-weight: 600;
  text-decoration: none;
}

.legal-contact a:hover {
  text-decoration: underline;
}

@media (max-width: 640px) {
  .legal-section {
    padding: 55px 0 70px;
  }

  .legal-body :deep(table) {
    display: block;
    overflow-x: auto;
  }
}
</style>
